import React from "react";
import { Box } from "@material-ui/core";
import Highcharts from "highcharts";
import StockLine from "components/visuals/StockLine";
import { useTheme } from "@material-ui/core/styles";
import { dateFormat } from "highcharts";
import { useAppSelector } from "store";
import { selectCustomCalendar, selectSalesReferenceDate, selectUseCustomCalendar } from "modules/customer/insights/portfolioNew/portfolioSlice";
import { selectSelectedStoreWeeklyRevenue } from "modules/customer/insights/portfolioNew/revenue/revenueSlice";
import numberFormatter from "utils/numberFormatter";
import { abs, min } from "mathjs";

const SalesHistory = () => {
    const theme = useTheme();
    const referenceDate = useAppSelector(selectSalesReferenceDate);
    const useCustomCalendar = useAppSelector(selectUseCustomCalendar);
    const customCalendar = useAppSelector(selectCustomCalendar);
    const salesHistory = useAppSelector(selectSelectedStoreWeeklyRevenue);
    const { isLoading, hasErrors, data } = salesHistory;
    const seriesColours = ["#42E8E0", "#1C76FD"];

    const series: Highcharts.SeriesOptionsType[] = [];

    data.forEach((weeklyRevenueSeries) => {
        const revenueValues = weeklyRevenueSeries.values;
        const actualsDataValues = revenueValues.map(revenue => [
            revenue.date.toUTC().toMillis(),
            revenue.forecastFlag ? null : revenue.sales
        ]);

        const forecastDataValues = revenueValues.map((revenue, index) => [
            revenue.date.toUTC().toMillis(),
            revenueValues[min(index + 1, revenueValues.length - 1)].forecastFlag ? revenue.sales : null
        ]);

        series.push({
            name: weeklyRevenueSeries.seriesName + " actuals",
            type: "spline",
            //@ts-ignore
            data: actualsDataValues,
            color: seriesColours[weeklyRevenueSeries.seriesID],
            showInNavigator: true,
            gapSize: 1,
            navigatorOptions: {
                color: seriesColours[weeklyRevenueSeries.seriesID]
            }
        });

        series.push({
            name: weeklyRevenueSeries.seriesName + " forecast",
            type: "spline",
            //@ts-ignore
            data: forecastDataValues,
            color: seriesColours[weeklyRevenueSeries.seriesID],
            dashStyle: "ShortDash",
            showInNavigator: true,
            gapSize: 1,
            navigatorOptions: {
                color: seriesColours[weeklyRevenueSeries.seriesID]
            }
        });
    });

    const xAxisMin = referenceDate.minus({ weeks: 52 }).toMillis();

    const getCustomLabel = (timestamp: string | number, axis?: Highcharts.Axis) => {
        if (typeof timestamp === "string" || customCalendar.length === 0) {
            return String(timestamp);
        }
        //@ts-ignore
        const tickInterval = axis?.tickInterval;
        const tickIntervalInDays = tickInterval / (1000 * 60 * 60 * 24);

        let difference = Number.MAX_VALUE;
        let dateIndex = 0;
        customCalendar.forEach((item, index) => {
            const newDifference = abs(timestamp - item.date.toMillis());
            if (newDifference < difference) {
                difference = newDifference;
                dateIndex = index;
            }
        });

        if (!tickIntervalInDays) {
            return customCalendar[dateIndex].financialYearLabel;
        }
        if (tickIntervalInDays < 28) {
            return String(customCalendar[dateIndex].weekLabel);
        }
        if (tickIntervalInDays < 90) {
            return String(customCalendar[dateIndex].periodLabel);
        }
        if (tickIntervalInDays < 180) {
            return customCalendar[dateIndex].quarterLabel;
        }
        return customCalendar[dateIndex].financialYearLabel;
    };

    const navigatorTickPositions = customCalendar.filter(item => item.periodNumber === 6 && item.weekNumber === 1).map(item => item.date.toMillis());

    const globalOptions = {
        lang: {
            rangeSelectorZoom: "Zoom"
        }
    };
    const options: Highcharts.Options = {
        yAxis: {
            title: {
                text: "Revenue"
            },
            labels: {
                //@ts-ignore
                formatter: function () { return numberFormatter.toGBP(this.value, 0); }
            }
        },
        series,
        xAxis: {
            type: 'datetime',
            min: xAxisMin,
            labels: useCustomCalendar ? {
                formatter: function () {
                    return getCustomLabel(this.value, this.axis);
                }
            } : {
                useHTML: true,
                style: {
                    color: theme.palette.common.white,
                    fontSize: "12px"
                },
                //@ts-ignore
                units: [["month", [1]]],
                dateTimeLabelFormats: {
                    month: "%b"
                }
            }
        },
        navigator: {
            series: {
                dataLabels: {
                    color: theme.palette.common.white
                },
                type: 'line',
                gapSize: 1
            },
            ...(useCustomCalendar && {
                xAxis: {
                    showFirstLabel: true,
                    labels: {
                        formatter: function () {
                            return getCustomLabel(this.value);
                        }
                    },
                    tickPositions: navigatorTickPositions
                }
            })
        },

        rangeSelector: {
            allButtonsEnabled: true,
            selected: undefined,
            buttons: [{
                type: "day",
                count: useCustomCalendar ? 28 : 31,
                text: useCustomCalendar ? "Period" : "Month",
                dataGrouping: {
                    forced: false,
                },
                preserveDataGrouping: true
            }, {
                type: "month",
                count: 3,
                text: "Quarter",
                preserveDataGrouping: true
            }, {
                type: "week",
                count: 52,
                text: useCustomCalendar ? "FY" : "Year",
                preserveDataGrouping: true
            }, {
                type: "all",
                text: "All",
                title: "View all",
                preserveDataGrouping: true
            }]
        },

        plotOptions: {
            series: {
                tooltip: {
                    valueDecimals: 2,//",.2f",
                    valuePrefix: "£"
                }
            }
        },

        legend: {
            enabled: true
        },

        tooltip: {
            useHTML: true,
            //@ts-ignore
            formatter: function () {
                if (!this.points) {
                    return "";
                }
                //@ts-ignore
                const timestamp = this.points[0].x as number;
                const title = useCustomCalendar
                    ? customCalendar.find(item => item.date.toMillis() === timestamp)?.weekLabel
                    : "Week from " + dateFormat("%A, %b %e, %Y", timestamp);
                let tooltipString: string = `<b>${title}</b><br/>`;
                //@ts-ignore
                this.points.forEach((point, index) => {
                    //@ts-ignore
                    if (this.points.length === series.length) {
                        if (index % 2 === 0) {
                            const string = `${point.series.name}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<b>${numberFormatter.toGBP(point.y)}</b><br/>`;
                            tooltipString += string;
                        }
                    } else {
                        const string = `${point.series.name}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<b>${numberFormatter.toGBP(point.y)}</b><br/>`;
                        tooltipString += string;
                    }

                });

                return tooltipString;
            }
        },

    };

    return (
        <Box data-cy="sales-history">
            <StockLine loading={isLoading} error={hasErrors} options={options} globalOptions={globalOptions} />
        </Box>
    );
};

export default SalesHistory;
